import React, { useContext, useEffect, useRef, useState } from "react";
import WebViewer, { WebViewerInstance } from "@pdftron/webviewer";
import * as Sentry from "@sentry/react";
import classNames from "classnames";

import css from "./FileViewer.module.scss";

type FileViewerContextProviderProps = {
  children: React.ReactNode;
};

type ViewerContext = {
  instance: WebViewerInstance | null;
  setInstance: React.Dispatch<React.SetStateAction<WebViewerInstance | null>>;
};

type FileViewerProps = {
  documentUrl: string;
  fileExtension?: string;
  enableAnnotations?: boolean;
  disabledElements?: string[];
};

export default function FileViewer({
  documentUrl,
  fileExtension = "pdf",
  enableAnnotations = true,
  disabledElements,
}: FileViewerProps) {
  const viewer = useRef<HTMLDivElement>(null);
  const isInitializing = useRef(false);
  const { instance, setInstance } = useFileViewerInstance();

  useEffect(() => {
    if (viewer.current === null) return;
    if (!instance && !isInitializing.current) {
      isInitializing.current = true;
      WebViewer(
        {
          path: "/apryse",
          licenseKey: window.apryseLicenseKey,
          enableAnnotations,
          disabledElements,
          // TODO: Migrate to Modular UI before legacy is removed in WebViewer 12 (mid-2026)
          // https://docs.apryse.com/web/guides/get-started/migrating-to-v11-modular-ui
          ui: "legacy",
        },
        viewer.current,
      )
        .then((instance) => {
          // Disabling embedded JS for security (APP-2326)
          instance.Core.disableEmbeddedJavaScript();

          setInstance(instance);

          const { documentViewer } = instance.Core;
          documentViewer.addEventListener("documentLoaded", function () {
            documentViewer.zoomTo(1.0);
          });

          instance.UI.hotkeys.off(instance.UI.hotkeys.Keys.P);
          // disable UI annotation editing (still allows for programmatic annotations)
          instance.UI.disableFeatures([instance.UI.Feature.Annotations]);

          if (disabledElements && !disabledElements.includes("searchPanel")) {
            // Add Ctrl + F functionality
            const handleKeyDown = (e: KeyboardEvent) => {
              if (e.ctrlKey && e.key === "f") {
                e.preventDefault();
                const isSearchPanelOpen = instance.UI.isElementOpen("searchPanel");

                if (isSearchPanelOpen) {
                  instance.UI.closeElements(["searchPanel"]);
                } else {
                  instance.UI.openElements(["searchPanel"]);
                }
              }
            };

            window.addEventListener("keydown", handleKeyDown);

            // Clean up event listener on unmount
            return () => {
              window.removeEventListener("keydown", handleKeyDown);
            };
          }
        })
        .catch((error) => {
          isInitializing.current = false;
          Sentry.captureException(error);
        });
    }
  }, [disabledElements, enableAnnotations, instance, setInstance]);

  useEffect(() => {
    if (instance) {
      instance.UI.loadDocument(documentUrl, { extension: fileExtension }).catch((error) =>
        Sentry.captureException(error),
      );
    }
  }, [documentUrl, fileExtension, instance]);

  return <div className={classNames("webviewer", css.fileViewer)} ref={viewer}></div>;
}

const FileViewerContext = React.createContext<ViewerContext | undefined>(undefined);

export function FileViewerContextProvider({ children }: FileViewerContextProviderProps) {
  const [instance, setInstance] = useState<WebViewerInstance | null>(null);

  return (
    <FileViewerContext.Provider value={{ instance, setInstance }}>
      {children}
    </FileViewerContext.Provider>
  );
}

export function useFileViewerInstance() {
  const context = useContext(FileViewerContext);

  if (!context) {
    throw new Error("useFileViewerInstance must be used within a FileViewerContextProvider");
  }

  return {
    instance: context.instance,
    setInstance: context.setInstance,
  };
}
